// Which lean forecasts gave up waiting for their detail, and the render gate that reads it.
//
// A lean row's detail is read by id when it comes on screen (commands/loadForecastDetail.ts).
// While that read is in flight the lane shows its loading line; once the read has failed for
// good, the lane draws what the lean row has instead of loading for the session. Kept out of the
// run store on purpose: `detailPending` must stay set on a row that was never filled, so the
// persister keeps refusing to save it. React-free: `features/timeline/useLaneCtx.js` subscribes.

import { contractHasAnswer } from "./progressSteps.js";

/** @type {ReadonlySet<string>} */
let failed = new Set();
/** @type {Set<() => void>} */
const listeners = new Set();

function publish(next) {
  failed = next;
  for (const listener of listeners) listener();
}

/**
 * Record that the detail of `runIds` could not be read.
 * @param {string[]} runIds
 */
export function markDetailFailed(runIds) {
  const add = runIds.filter((id) => !failed.has(id));
  if (add.length) publish(new Set([...failed, ...add]));
}

/**
 * Forget a failure, before `runIds` are read again.
 * @param {string[]} runIds
 */
export function clearDetailFailed(runIds) {
  if (!runIds.some((id) => failed.has(id))) return;
  const next = new Set(failed);
  for (const id of runIds) next.delete(id);
  publish(next);
}

/** The runs whose detail could not be read; a new Set on every change. */
export function getDetailFailed() {
  return failed;
}

/**
 * Calls `listener` whenever the set changes; returns the unsubscribe.
 * @param {() => void} listener
 */
export function subscribeDetailFailed(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset. */
export function resetDetailFailed() {
  publish(new Set());
}

/**
 * True while a run's stored detail has not been read: its history, events and report. Anything
 * built on them (a rerun's superseded vintage, its merged events) would be built on nothing and
 * saved over the real ones. A run that completed locally is no exception: its completion wrote
 * its new answer, not the history before it.
 * @param {{ detailPending?: boolean } | null | undefined} run
 */
export function lacksDetail(run) {
  return !!run?.detailPending;
}

/**
 * True when a projected root is a lean row still waiting for its detail with nothing of its own
 * to draw. `detailPending` alone is not that: it is a persistence guard, also set on a Sayer
 * stand-in (which carries its contract), on a run that completed locally inside the boot window,
 * and on running rows whose progress UI must stay up.
 * @param {{ runId?: string, detailPending?: boolean, detailLocal?: boolean, status?: string,
 *   serverPrediction?: number | null,
 *   contract?: unknown, events?: unknown[] | null, listSummary?: { contract?: unknown } | null }} root
 * @param {ReadonlySet<string>} [detailFailed] runs whose detail read gave up
 */
export function awaitsDetail(root, detailFailed) {
  if (!root.detailPending || root.detailLocal) return false;
  if (root.runId && detailFailed?.has(root.runId)) return false;
  // A first run has nothing to wait for: its progress is all there is to draw. A rerun of a
  // forecast that has an answer (its lean summary holds one, or it keeps its last prediction)
  // still waits, or its branches would draw before its own forecast does. A first run's summary
  // can hold just its preprocessed contract, with no answer yet.
  const running = root.status === "running" || root.status === "preprocessing";
  const hasAnswer =
    Number.isFinite(root.serverPrediction) || contractHasAnswer(root.listSummary?.contract);
  if (running && !hasAnswer) return false;
  return root.contract == null && (root.events == null || root.events.length === 0);
}
